import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Phone, ShieldCheck } from "lucide-react";
import { SERVICES, COMPANY } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import { PageHero, PillBadge, CTASection, SERVICE_ICONS } from "../components/Shared";
import { useQuote } from "../components/QuoteModal";

export default function ServiceDetail() {
  const { slug } = useParams();
  const ref = useRevealGroup();
  const { openQuote } = useQuote();

  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const Icon = SERVICE_ICONS[service.icon] || ShieldCheck;
  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div ref={ref} key={slug}>
      <PageHero badge={service.heroLine} title={service.title} subtitle={service.intro} image={service.image} />

      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-[1fr_360px]">
          {/* Main content */}
          <div className="space-y-16">
            {service.sections.map((sec, idx) => (
              <div key={sec.heading} data-reveal className={`grid grid-cols-1 items-center gap-10 ${idx % 2 === 0 ? "lg:grid-cols-[1.2fr_1fr]" : "lg:grid-cols-[1fr_1.2fr]"}`}>
                <div className={idx % 2 === 1 ? "lg:order-2" : ""}>
                  <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0B2748] sm:text-3xl">{sec.heading}</h2>
                  <p className="mt-4 text-[15.5px] leading-relaxed text-[#526A7D]">{sec.body}</p>
                  <ul className="mt-6 space-y-3">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[15px] text-[#193B58]">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]" /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`${idx % 2 === 1 ? "lg:order-1" : ""} overflow-hidden rounded-3xl`}>
                  <img src={service.image} alt={sec.heading} className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
              </div>
            ))}

            {/* Why us */}
            <div data-reveal className="rounded-3xl bg-[#006FB9] p-10 text-white sm:p-12">
              <PillBadge light>Your satisfaction is 100% guaranteed</PillBadge>
              <h3 className="mt-4 font-heading text-2xl font-extrabold sm:text-3xl">Why choose Abhi Insurance Agency?</h3>
              <ul className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {service.why.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-[15px] leading-relaxed text-[#D9ECF7]">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#B9DFF3]" /> {w}
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA strip */}
            <div data-reveal className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-[#E1ECF3] bg-[#F4F7FB] p-8 lg:flex-row lg:items-center">
              <p className="max-w-xl text-[15.5px] font-medium leading-relaxed text-[#193B58]">{service.ctaText}</p>
              <a href={COMPANY.phoneHref} className="btn-primary group shrink-0" data-testid="service-call-btn">
                <Phone className="h-4 w-4" /> Call {COMPANY.phone}
              </a>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            <div data-reveal className="rounded-2xl bg-white p-7 shadow-[0_4px_24px_rgba(0,111,185,0.09)]">
              <span className="grid h-[52px] w-[52px] place-items-center rounded-2xl bg-[#E2F2FA]">
                <Icon className="h-6 w-6 text-[#006FB9]" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-[#0B2748]">Get a {service.title} quote</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#526A7D]">Free consultation, best available rates, no hidden fees.</p>
              <button onClick={() => openQuote(service.title)} className="btn-primary group mt-5 w-full justify-center" data-testid="sidebar-quote-btn">
                Get a Free Quote <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
            <div data-reveal className="rounded-2xl border border-[#E1ECF3] bg-white p-7">
              <h4 className="font-heading text-base font-bold text-[#0B2748]">Other Services</h4>
              <ul className="mt-4 space-y-1">
                {others.map((o) => (
                  <li key={o.slug}>
                    <Link to={`/services/${o.slug}`} className="group flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium text-[#294A65] transition-colors duration-200 hover:bg-[#EEF6FB] hover:text-[#006FB9]">
                      {o.title}
                      <ArrowRight className="h-4 w-4 -translate-x-1 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div data-reveal className="rounded-2xl bg-[#E2F2FA] p-7">
              <p className="font-heading text-base font-bold text-[#0B2748]">Talk to a human, 7 days a week</p>
              <a href={COMPANY.phoneHref} className="mt-3 flex items-center gap-2.5 font-heading text-lg font-extrabold text-[#006FB9]">
                <Phone className="h-5 w-5" /> {COMPANY.phone}
              </a>
              <p className="mt-1.5 text-sm text-[#45647C]">{COMPANY.hours}</p>
            </div>
          </aside>
        </div>
      </section>

      <CTASection title={`Ready for ${service.title}?`} subtitle={service.ctaText} />
    </div>
  );
}
